/**
 * Vue Router 路由配置
 *
 * 职责：
 * - 定义应用的全部路由规则，包括登录页和主布局下的子页面
 * - 实现路由守卫：未登录用户自动跳转登录页；已登录用户访问登录页自动跳转仪表盘
 * - 主布局下的子页面统一要求登录认证（requiresAuth: true）
 * - 用户管理页面额外要求管理员权限（requiresAdmin: true，守卫中可扩展检查）
 *
 * 路由结构：
 * /login               → 登录页（无需认证）
 * /                    → 主布局（需认证），重定向到 /dashboard
 *   /dashboard         → 仪表盘
 *   /knowledge-bases   → 知识库列表
 *   /knowledge-bases/:id → 知识库详情
 *   /documents/:id     → 文档详情
 *   /chat              → AI 对话
 *   /agents            → 机器人管理
 *   /models            → 模型维护
 *   /users             → 用户管理（需管理员）
 */
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

/* 路由表定义 */
const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/pages/Login.vue'),
    /* 登录页不需要认证 */
    meta: { requiresAuth: false },
  },
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    /* 主布局下的所有子页面默认需要认证 */
    meta: { requiresAuth: true },
    /* 访问根路径时重定向到仪表盘 */
    redirect: '/dashboard',
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/pages/Dashboard.vue'),
        /* 租户数据面（33 号 §8）：平台超管经路由守卫重定向 /tenants */
        meta: { tenantPlane: true },
      },
      {
        path: 'knowledge-bases',
        name: 'KnowledgeBases',
        component: () => import('@/pages/KnowledgeBases.vue'),
        meta: { tenantPlane: true },
      },
      {
        path: 'knowledge-bases/:id',
        name: 'KnowledgeBaseDetail',
        component: () => import('@/pages/KnowledgeBaseDetail.vue'),
        /* 将路由参数 id 作为 props 传入组件 */
        props: true,
        meta: { tenantPlane: true },
      },
      {
        path: 'documents/:id',
        name: 'DocumentDetail',
        component: () => import('@/pages/DocumentDetail.vue'),
        props: true,
        meta: { tenantPlane: true },
      },
      {
        path: 'chat',
        name: 'Chat',
        component: () => import('@/pages/Chat.vue'),
        meta: { tenantPlane: true },
      },
      {
        path: 'agents',
        name: 'Agents',
        component: () => import('@/pages/Agents.vue'),
        meta: { tenantPlane: true },
      },
      {
        path: 'models',
        name: 'Models',
        component: () => import('@/pages/Models.vue'),
        /* 模型维护按权限点显隐（33 号：平台超管默认持有；租户 admin 需本租户被授权） */
        meta: { requiresPerm: 'model.manage' },
      },
      {
        path: 'tenants',
        name: 'Tenants',
        component: () => import('@/pages/Tenants.vue'),
        /* 租户管理按权限点（33 号 tenant.manage=仅平台超管；后端 RequirePerm 硬校验兜底） */
        meta: { requiresPerm: 'tenant.manage' },
      },
      {
        path: 'users',
        name: 'Users',
        component: () => import('@/pages/Users.vue'),
        /* 用户管理按权限点（33 号 user.manage）；用户数据属租户数据面，平台超管不进 */
        meta: { requiresPerm: 'user.manage', tenantPlane: true },
      },
      {
        path: 'logs',
        name: 'Logs',
        component: () => import('@/pages/Logs.vue'),
        /* 日志浏览按权限点（33 号 monitor.view） */
        meta: { requiresPerm: 'monitor.view' },
      },
      {
        path: 'audit-logs',
        name: 'AuditLogs',
        component: () => import('@/pages/AuditLogs.vue'),
        /* 审计台账按权限点（48 号 D3 audit.view：admin 持有；平台面专属——不加 tenantPlane
           否则平台超管被重定向踢走，页面内再按 isPlatformSuper 前置提示） */
        meta: { requiresPerm: 'audit.view' },
      },
      {
        path: 'usage',
        name: 'Usage',
        component: () => import('@/pages/Usage.vue'),
        /* 用量看板需要管理员权限 */
        meta: { requiresAdmin: true, tenantPlane: true },
      },
      {
        path: 'evaluation',
        name: 'Evaluation',
        component: () => import('@/pages/Evaluation.vue'),
        /* 评测门禁按权限点（33 号 eval.run：admin/editor 持有） */
        meta: { requiresPerm: 'eval.run', tenantPlane: true },
      },
      {
        path: 'agent-workflows',
        name: 'AgentWorkflows',
        component: () => import('@/pages/AgentWorkflows.vue'),
        /* 工作流编排管理需要管理员权限 */
        meta: { requiresAdmin: true, tenantPlane: true },
      },
      {
        path: 'delete-requests',
        name: 'DeleteRequests',
        component: () => import('@/pages/DeleteRequests.vue'),
        /* 文档删除工单审批按权限点（33 号 delete.approve；MCP 工单式删除） */
        meta: { requiresPerm: 'delete.approve', tenantPlane: true },
      },
      {
        path: 'permission-matrix',
        name: 'PermissionMatrix',
        component: () => import('@/pages/PermissionMatrix.vue'),
        /* 权限矩阵只读页（33 号静态 RBAC 可视化）：所有登录身份可看——
           矩阵本身是文档级信息，不属租户数据面（平台超管同样可进） */
      },
    ],
  },
]

/* 创建路由实例，使用 HTML5 History 模式 */
const router = createRouter({
  history: createWebHistory(),
  routes,
})

/**
 * 全局前置路由守卫
 *
 * 检查逻辑：
 * 1. 目标页面需要认证（requiresAuth !== false）且本地无 token → 跳转登录页，携带 redirect 参数
 * 2. 已登录用户访问登录页 → 跳转仪表盘
 * 3. 其他情况正常放行
 *
 * 注意：requiresAdmin 的细粒度权限检查可在后续迭代中扩展
 */
/**
 * 从 localStorage 的 access_token 中解析 JWT payload（仅供前端路由守卫做 UX 级拦截；
 * 真正的权限强制由后端 RoleMiddleware/RequirePerm 兜底，客户端 payload 可被篡改但不影响安全）。
 */
function getTokenPayload(): { role?: string; tenant_id?: string } {
  const token = localStorage.getItem('access_token')
  if (!token) return {}
  try {
    // JWT payload 是 base64url 编码的 JSON，先转 base64 再 atob 解码
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(part)) as { role?: string; tenant_id?: string }
  } catch {
    return {}
  }
}

/** 从 token 解析 role（见 getTokenPayload 安全说明） */
function getRoleFromToken(): string {
  return getTokenPayload().role ?? ''
}

/** 平台超管判定（22 号平面分离）：role=admin 且 tenant_id 为空 */
function isPlatformSuperFromToken(): boolean {
  const p = getTokenPayload()
  return p.role === 'admin' && !p.tenant_id
}

router.beforeEach(async (to, _from, next) => {
  const token = localStorage.getItem('access_token')

  /* 需要认证但未登录 → 跳转登录页并记录原始目标路径 */
  if (to.meta.requiresAuth !== false && !token) {
    return next({ name: 'Login', query: { redirect: to.fullPath } })
  }

  /* 33 号 §8 平台面极简导航：平台超管访问租户数据面（tenantPlane）页面时
     一律重定向租户管理——平台超管不进入任何租户数据面（含根路径默认页
     /dashboard，同样经此分支落到 /tenants）。 */
  if (token && to.meta.tenantPlane && isPlatformSuperFromToken()) {
    return next({ name: 'Tenants' })
  }

  /* P1-4：管理员页面（requiresAdmin）仅 admin 角色可进，非 admin 跳回仪表盘。
     后端 RoleMiddleware 已对 /monitor 等 API 兜底，此处为 UX 层拦截。 */
  if (to.meta.requiresAdmin && getRoleFromToken() !== 'admin') {
    return next({ name: 'Dashboard' })
  }

  /* 33 号决策 3：权限点页面（requiresPerm）按 /auth/permissions 集合判定——
     与后端 RequirePerm 同一枚举字符串，杜绝两端漂移；隐藏只是体验层。
     注意动态引入 store：stores/auth.ts 静态 import 本模块（router），
     顶层再静态引回会构成 ESM 循环依赖。 */
  if (to.meta.requiresPerm && token) {
    const { useAuthStore } = await import('@/stores/auth')
    const authStore = useAuthStore()
    /* 权限点未加载（如刷新后首跳）先拉取，失败视为无权限 */
    if (authStore.permissions === null) {
      await authStore.fetchPermissions()
    }
    if (!authStore.hasPerm(to.meta.requiresPerm as string)) {
      return next({ name: 'Dashboard' })
    }
  }

  /* 已登录用户访问登录页 → 按身份平面跳转：平台超管落租户管理，租户用户落仪表盘 */
  if (to.name === 'Login' && token) {
    return next({ name: isPlatformSuperFromToken() ? 'Tenants' : 'Dashboard' })
  }

  next()
})

export default router
